import { useEffect, useState } from 'react';

import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { fetchArticleReviews } from '@/domains/productDiscovery/ArticleReviews/articleReviews.api';
import type { Review } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';

import { GTM_ARTICLE_REVIEWS } from './gtmEvents';

export type Order = 'createdAt' | 'upVoteCount' | 'reviewScore';

export interface UseArticleReviewsReturn {
  reviews: Review[];
  fetchMoreReviews: () => void;
  total: number;
  starsFilter: number | null;
  toggleStarsFilter: (rating: number) => void;
  clearStarsFilter: () => void;
  onlyB2b: boolean;
  toggleB2bOnly: () => void;
  orderBy: string;
  orderReview: (order: Order) => void;
  hasMoreReviews: boolean;
  fetched: boolean;
  fetching: boolean;
}

function trackBrowseReviews(
  page: number,
  filter: number | null,
  sort: string,
  onlyB2B: boolean,
  reviews: Review[],
  total: number,
) {
  Gtm.push(GTM_ARTICLE_REVIEWS(page, filter, onlyB2B, sort, reviews, total));
}

export const useArticleReviews = (
  articleId: number,
  limit: number,
): UseArticleReviewsReturn => {
  const [pagination, setPagination] = useState<{
    pages: number;
    total: number;
  }>({
    pages: 0,
    total: 0,
  });
  const [reviews, setReviews] = useState<Review[]>([]);
  const [starsFilter, setStarsFilters] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [onlyB2b, setOnlyB2B] = useState<boolean>(false);
  const [fetched, setFetched] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [orderBy, setOrderBy] = useState<Order>('createdAt');
  const hasMoreReviews = page < pagination.pages;

  useEffect(() => {
    const currentPage = page;

    setFetching(true);
    fetchArticleReviews(
      articleId,
      limit,
      currentPage,
      onlyB2b,
      starsFilter,
      orderBy,
    )
      .then((result) => {
        setPagination({
          pages: result.pagination.pages,
          total: result.pagination.items,
        });
        trackBrowseReviews(
          currentPage,
          starsFilter,
          orderBy,
          onlyB2b,
          result.reviews,
          result.pagination.items,
        );
        setReviews((prev) => [...prev, ...result.reviews]);
      })
      .finally(() => {
        setFetching(false);
        setFetched(true);
      });
  }, [articleId, limit, onlyB2b, orderBy, starsFilter, page]);

  const fetchMoreReviews = () => setPage(page + 1);

  const toggleStarsFilter = (rating: number) => {
    setStarsFilters(rating);
    setPage(1);
    setReviews([]);
  };

  const clearStarsFilter = () => {
    setStarsFilters(null);
    setPage(1);
    setReviews([]);
  };

  const setOrder = (order: Order) => {
    if (order === orderBy) return;

    setOrderBy(order);
    setReviews([]);
  };

  const toggleB2bOnly = () => {
    setPage(1);
    setReviews([]);
    setOnlyB2B(!onlyB2b);
  };

  return {
    reviews,
    hasMoreReviews,
    fetching,
    fetched,
    fetchMoreReviews,
    total: pagination.total,
    starsFilter,
    onlyB2b,
    toggleB2bOnly,
    toggleStarsFilter,
    clearStarsFilter,
    orderBy,
    orderReview: setOrder,
  };
};
